import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Card, Space, Spin, Tag, Typography } from "antd";
import ImageGrid from "./MarketingImageGrid";
import { FolderOutlined } from "@ant-design/icons";
import {
  ALL_GAMES_FILTER,
  GENERAL_GAME_ID,
  MARKETING_GAMES,
} from "./marketingGameConfig";
import {
  filterAndGroupAssets,
  getAssetCategory,
  resolveGameId,
} from "./marketingGameUtils";

type Api_ImageData = {
  [key: string]: { width: number; height: number } | Api_ImageData;
};

type ApiResponse = {
  Images: Api_ImageData;
  Videos: Api_ImageData;
};

export type ImageData = {
  width: number;
  height: number;
  altText: string;
  imageUrl: string;
  assetPath: string;
  gameId: string;
  category: string;
};

export type VideoData = {
  videoUrl: string;
  width: number;
  height: number;
  assetPath: string;
  gameId: string;
  category: string;
};

const BASE_URL = "https://cdn-marketing.herozonevr.com/";
const { CheckableTag } = Tag;

export const Marketing = () => {
  const [images, setImages] = useState<
    {
      folderName: string;
      images: ImageData[];
    }[]
  >([]);
  const [videos, setVideos] = useState<
    { folderName: string; videos: VideoData[] }[]
  >([]);
  const [selectedGame, setSelectedGame] = useState(ALL_GAMES_FILTER);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImageList();
  }, []);

  const fetchImageList = async () => {
    try {
      const { data } = await axios.get<ApiResponse>(
        "/api/marketing_images",
        {}
      );
      const groupedImages = flattenImageData(data.Images || {});

      setImages(groupedImages);
      setVideos(flattenVideoData(data.Videos || {}));
    } catch (error) {
      console.error("Error fetching images", error);
    } finally {
      setLoading(false);
    }
  };

  const flattenImageData = (
    imageData: Api_ImageData,
    parentKey: string = ""
  ): {
    folderName: string;
    images: ImageData[];
  }[] => {
    let groupedImages: {
      folderName: string;
      images: ImageData[];
    }[] = [];

    Object.keys(imageData).forEach((key) => {
      const value = imageData[key];
      if (
        typeof value === "object" &&
        "width" in value &&
        "height" in value &&
        typeof value.width === "number" &&
        typeof value.height === "number"
      ) {
        const assetPath = `Images/${parentKey}${key}`;
        const imageUrl = `${BASE_URL}${assetPath}`;
        const folderName = parentKey.split("/").filter(Boolean).pop() || key;
        const existingFolder = groupedImages.find(
          (folder) => folder.folderName === folderName
        );

        const imageObject: ImageData = {
          imageUrl,
          altText: key,
          width: value.width,
          height: value.height,
          assetPath,
          gameId: resolveGameId(assetPath),
          category: getAssetCategory(assetPath),
        };

        if (existingFolder) {
          existingFolder.images.push(imageObject);
        } else {
          groupedImages.push({
            folderName,
            images: [imageObject],
          });
        }
      } else if (typeof value === "object") {
        groupedImages = groupedImages.concat(
          flattenImageData(value as Api_ImageData, `${parentKey}${key}/`)
        );
      }
    });

    return groupedImages;
  };

  const flattenVideoData = (
    videoData: Api_ImageData | VideoData,
    parentKey: string = ""
  ): {
    folderName: string;
    videos: VideoData[];
  }[] => {
    let groupedVideos: {
      folderName: string;
      videos: VideoData[];
    }[] = [];

    Object.keys(videoData).forEach((key) => {
      const value = (videoData as Api_ImageData)[key];
      if (
        typeof value === "object" &&
        "width" in value &&
        "height" in value &&
        typeof value.width === "number" &&
        typeof value.height === "number"
      ) {
        const assetPath = `Videos/${parentKey}${key}`;
        const videoUrl = `${BASE_URL}${assetPath}`;
        const folderName = parentKey.split("/").filter(Boolean).pop() || key;

        const existingFolder = groupedVideos.find(
          (folder) => folder.folderName === folderName
        );

        const videoObject: VideoData = {
          videoUrl,
          width: value.width || 300,
          height: value.height + 50 || 300,
          assetPath,
          gameId: resolveGameId(assetPath),
          category: getAssetCategory(assetPath),
        };

        if (existingFolder) {
          existingFolder.videos.push(videoObject);
        } else {
          groupedVideos.push({
            folderName,
            videos: [videoObject],
          });
        }
      } else if (typeof value === "object") {
        groupedVideos = groupedVideos.concat(
          flattenVideoData(value as Api_ImageData, `${parentKey}${key}/`)
        );
      }
    });

    return groupedVideos;
  };

  const filteredVideos = useMemo(
    () =>
      filterAndGroupAssets(
        videos.map((folder) => ({
          folderName: folder.folderName,
          items: folder.videos,
        })),
        selectedGame
      ),
    [videos, selectedGame]
  );

  const filteredImages = useMemo(
    () =>
      filterAndGroupAssets(
        images.map((folder) => ({
          folderName: folder.folderName,
          items: folder.images,
        })),
        selectedGame
      ),
    [images, selectedGame]
  );

  const gameFilters = [
    { id: ALL_GAMES_FILTER, label: "All" },
    ...MARKETING_GAMES,
    { id: GENERAL_GAME_ID, label: "Brand & General" },
  ];

  if (loading) return <Spin size="large" />;

  const hasResults = filteredVideos.length > 0 || filteredImages.length > 0;

  return (
    <div style={{ width: "85%" }}>
      <Space style={{ width: "100%" }} direction="vertical" size="large">
        <Typography.Title level={2}>Marketing Assets</Typography.Title>
        <Typography.Link
          href="https://drive.google.com/drive/folders/1ts15cUDgrhbAMRXsa3DyjVTNKY2QEeSk?usp=drive_link"
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: "1.1em" }}
        >
          <FolderOutlined style={{ fontSize: "1.1em" }} /> Game Guides
        </Typography.Link>

        <div>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            Filter by game
          </Typography.Text>
          <Space size={[8, 8]} wrap>
            {gameFilters.map((game) => (
              <CheckableTag
                key={game.id}
                checked={selectedGame === game.id}
                onChange={(checked) => {
                  if (checked) setSelectedGame(game.id);
                }}
              >
                {game.label}
              </CheckableTag>
            ))}
          </Space>
        </div>

        {!hasResults && (
          <Typography.Text type="secondary">
            No assets found for this game.
          </Typography.Text>
        )}

        {filteredVideos.map((folder) => (
          <Card title={folder.folderName} bordered={false} key={folder.folderName}>
            <ImageGrid images={folder.items} />
          </Card>
        ))}
        {filteredImages.map((folder) => (
          <Card title={folder.folderName} bordered={false} key={folder.folderName}>
            <ImageGrid images={folder.items} />
          </Card>
        ))}
      </Space>
    </div>
  );
};
